import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import {
  type AxisBase2D,
  DpiHelper,
  NumericAxis,
  NumericLabelProvider,
  NumberRange,
  EAutoRange,
  XyDataSeries,
  XyyDataSeries,
  FastBandRenderableSeries,
  FastLineRenderableSeries,
  XyScatterRenderableSeries,
  EllipsePointMarker,
  SquarePointMarker,
  SpritePointMarker,
  BoxAnnotation,
  NativeTextAnnotation,
  LineAnnotation,
  Thickness,
  VerticalLineAnnotation,
  ECoordinateMode,
  EHorizontalAnchorPoint,
  EVerticalAnchorPoint,
  MouseWheelZoomModifier,
  XAxisDragModifier,
  YAxisDragModifier,
  ZoomPanModifier,
  ZoomExtentsModifier,
} from 'scichart';
import { Link } from '@carbon/react';
import {
  SciChartView,
  AXIS_TITLE_FONT_SIZE,
  AXIS_LABEL_FONT_SIZE,
  type SciChartInitContext,
} from 'components/charts/scichart';
import {
  buildProfilePlan,
  deepEdge,
  depthAtDistance,
  crossingSeriesKey,
  depthExtentOf,
  formatSectionDistance,
  formatAxisTick,
  type ProfileCrossing,
  type ProfileFault,
  type ProfileBoundary,
  type ProfileStickInput,
  type PlanLabel,
  type ProfilePlan,
  type ProfilePoint,
  type ProfileSeries,
  type ProfileVertex,
  hoverGroupsAt,
  hasNoMeasuredDepth,
  wellAt,
  type HoverGroup,
} from './profile-plan';
import {
  BODY_RADIUS_SHARE,
  WELL_SYMBOL_SIZE,
  wellSymbolSize,
  isFilledSymbol,
  symbolForCrossing,
  wellSymbolImage,
  wellSymbolKey,
  type WellSymbol,
} from './well-symbols';
import styles from './cross-section-profile-chart.module.scss';

/**
 * How much of the bounding horizon's colour an interval fill carries. Translucent on
 * purpose: horizons, wells and fault crossings all have to stay legible through it.
 */
const FILL_OPACITY = 0.22;

/**
 * A fault crossing, drawn as a band because a mapped fault has real width. Orange at
 * low opacity: the horizons and wells inside the band still have to be read.
 */
const FAULT_FILL = 'rgba(232, 97, 29, 0.18)';
const FAULT_STROKE = 'rgba(232, 97, 29, 0.5)';

/** How much wider than the symbol the focus ring is drawn, so it reads as a ring. */
const FOCUS_RING_GROWTH = 8;

/**
 * A county line: a rule the height of the plot, in the ground surface's neutral grey —
 * context the section passes through, not data it holds. Solid rather than dashed,
 * because unlike a vertex guide it marks something real on the ground.
 */
const COUNTY_STROKE = '#525252';
const COUNTY_LABEL_COLOR = '#525252';
const COUNTY_LABEL_FONT_SIZE = 11;

/** Just below the top edge, clear of the vertex letters that sit on it. */
const COUNTY_LABEL_Y = 0.04;

/** Breathing room above and below the deepest and shallowest thing drawn. */
const DEPTH_MARGIN = 0.12;

/**
 * How far in from the edge of the view an interval name sits, as a share of the
 * distance on show. Enough to clear the axis without floating away from the edge it
 * is pinned to.
 */
const LABEL_VIEW_INSET_FRACTION = 0.02;

/** Extra room at the top of the plot for the three lines a bore writes above itself. */
const STICK_LABEL_HEADROOM = 0.14;

/**
 * A defining well's bore. Thicker than a horizon, because it is the thing the section
 * was built from rather than something the section found.
 */
const STICK_THICKNESS = 3;
const STICK_LABEL_FONT_SIZE = 11;

/**
 * The class symbol at a bore's surface, at the size a crossing's is drawn. Smaller read
 * as no symbology at all: the meaning is in what is drawn inside the body — a shut-in's
 * X, an expired permit's slash — and at eight pixels that closes up into a dot.
 */
/**
 * How far a bore's label stack sits above its top marker, in device pixels. The marker
 * straddles the bore's top end, so a label anchored exactly there sat on the symbol's
 * shoulders.
 */
const STICK_LABEL_LIFT_PX = 10;
const STICK_TD_TICK_WIDTH = 11;
const STICK_TD_TICK_HEIGHT = 3;

/**
 * Where a bore's name is pinned. A well at either end of the section has its name
 * anchored on the inward side, so the plot edge does not cut it in half.
 */
const STICK_LABEL_ANCHORS = {
  left: EHorizontalAnchorPoint.Left,
  center: EHorizontalAnchorPoint.Center,
  right: EHorizontalAnchorPoint.Right,
} as const;

/** Ground surface: a neutral grey, so it reads as context rather than a horizon. */
const GROUND_STROKE = '#525252';
const LINE_THICKNESS = 2;

/**
 * The shared position, as a rule down the plot. Dashed, so it reads as a pointer
 * rather than as something the section contains.
 */
const INDICATOR_STROKE = '#161616';

/** The focused well, spotlit rather than recoloured: it is still the same well. */
const FOCUS_STROKE = '#8a3ffc';
const MARKER_STROKE_COLOR = '#161616';

/** Vertex guides and their letters, faint enough to sit under the data. */
const GUIDE_STROKE = '#a8a8a8';
const GUIDE_DASH = [3, 3];
const GUIDE_OPACITY = 0.8;
const GUIDE_LETTER_COLOR = '#525252';
const GUIDE_LETTER_FONT_SIZE = 11;

/** A / A′ at the section's ends, on their own row above the data. */
const END_LABEL_COLOR = '#161616';
const END_LABEL_FONT_SIZE = 16;

// Relative heights: the letters sit just below the top of the plot, with the end
// letters a touch lower than the guide letters so the two rows never collide.
const END_LABEL_Y = 0.095;
const GUIDE_LETTER_Y = 0.078;

const INTERVAL_LABEL_FONT_SIZE = 11;

/**
 * The least two interval names may sit apart, in CSS pixels — a line of text plus half
 * again. Pixels because that is where the collision happens: a share of the depth on
 * show equals a line of text at one plot height only, and this drawer opens shorter.
 */
const INTERVAL_LABEL_GAP_PX = Math.round(INTERVAL_LABEL_FONT_SIZE * 1.8);

/**
 * A colour at a given opacity, whichever way it arrives.
 *
 * A rule's swatch can carry its own opacity — the rail stores those as `rgba()`, the
 * way the Type Curve rail does — so a fill has to scale what is already there rather
 * than assume six hex digits. For hex, alpha goes last (`#rrggbbaa`): written
 * alpha-first the fills came out as near-opaque dark bands, because the first six
 * digits were read as the colour.
 *
 * @param {string} color - A `#rrggbb` or `rgba()` colour.
 * @param {number} opacity - 0 to 1, applied on top of any the colour carries.
 * @return {string} The colour at that opacity.
 */
const withOpacity = (color: string, opacity: number): string => {
  const rgba = /^rgba?\(([^)]+)\)$/i.exec(color.trim());
  if (rgba) {
    const parts = rgba[1].split(',').map((part) => part.trim());
    const [red, green, blue] = parts;
    const carried = parts[3] === undefined ? 1 : Number(parts[3]);
    const alpha = (Number.isFinite(carried) ? carried : 1) * opacity;
    // Rounded rather than formatted: this is a colour channel, not a number on screen.
    return `rgba(${red}, ${green}, ${blue}, ${Number(alpha.toFixed(2))})`;
  }
  const alpha = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0');
  return `#${color.replace('#', '')}${alpha}`;
};

/**
 * Where a pointer event lands in the PLOT's own coordinates, in CSS pixels.
 *
 * The canvas is bigger than the plot: axes and their labels take a margin around it,
 * ten pixels of it on the left. A coordinate calculator answers in the plot's space, so
 * a canvas-relative x read ten feet-worth to the right of the pointer — which is the
 * indicator line sitting beside the cursor, and the well picks that kept missing.
 *
 * @param {object} surface - The SciChart surface.
 * @param {HTMLElement} canvas - The canvas the event was raised on.
 * @param {MouseEvent} event - The pointer event.
 * @return {object} The point, relative to the plot area.
 */
const plotPointFrom = (
  surface: { getSeriesViewRectPadding: (scaled?: boolean) => Thickness },
  canvas: HTMLElement,
  event: MouseEvent
): { x: number; y: number } => {
  const bounds = canvas.getBoundingClientRect();
  const padding = surface.getSeriesViewRectPadding(false);
  return {
    x: event.clientX - bounds.left - padding.left,
    y: event.clientY - bounds.top - padding.top,
  };
};

/**
 * Drops an axis's cached tick labels, so the next render reformats them.
 *
 * @param {AxisBase2D} axis - The axis to relabel.
 */
const invalidateLabels = (axis: AxisBase2D): void => {
  const provider = axis.labelProvider as NumericLabelProvider;
  provider.invalidateCache?.();
};

/**
 * A tooltip depth, whole feet with separators, or an em dash for a value the data
 * does not have.
 *
 * @param {number | null} valueFt - The depth.
 * @return {string} The text.
 */
const hoverValue = (valueFt: number | null): string => {
  return valueFt === null ? '—' : `${formatAxisTick(valueFt, 0)} ft`;
};

export interface CrossSectionProfileChartProps {
  /** The horizon stack, in stratigraphic order. */
  series: ProfileSeries[];
  /** Ground-surface elevation along the section, when it was asked for. */
  ground?: ProfilePoint[] | null;
  showGround?: boolean;
  /** The section's vertices, as the map lettered them. */
  vertices?: ProfileVertex[];
  /**
   * The drawn line's full extent. Pinning to it keeps the end letters on the
   * section's real endpoints; null lets the axis close in on the data.
   */
  xRange?: [number, number] | null;
  depthAxisTitle: string;
  invertDepthAxis?: boolean;
  /**
   * Depths read against a datum rather than a true reference. Surface features are
   * left unclipped there: no depth on that axis is the ground.
   */
  flattened?: boolean;
  distanceUnit?: 'ft' | 'mi';
  /** Read the section right to left, which swaps which end is A. */
  flipped?: boolean;
  /** Write each grid's interval name at one end of its own horizon. */
  showLabels?: boolean;
  /** Guide the end vertices too — see `ProfilePlanInput.guideEnds`. */
  guideEnds?: boolean;
  /** Shade each interval between adjacent horizons. */
  showFills?: boolean;
  /**
   * Bumped to put the plot back to the extent the section opened at. A counter
   * rather than a flag, so two resets in a row are two resets.
   */
  resetSignal?: number;
  /**
   * Dragging an axis gutter rescales just that axis, as the Inventory gun barrel chart does.
   * Opt-in: the Map View's drawer keeps the gestures it has, and only the Wellbore Geometry
   * tearsheet asks for this.
   */
  axisZoom?: boolean;
  /** The calculated crossings to mark, already filtered by the header's switch. */
  crossings?: ProfileCrossing[];
  /** The calculated fault spans to band, already filtered the same way. */
  faults?: ProfileFault[];
  /**
   * The wells that define the plane, as bores at their vertices. Well to Well only —
   * these wells never appear among the crossings, because they do not cross a plane
   * they define.
   */
  sticks?: ProfileStickInput[];
  /**
   * The county lines the plane crosses, when they have been asked for and are shown.
   */
  boundaries?: ProfileBoundary[];
  /** The well singled out, emphasised wherever it appears. */
  focusedWellUwi?: string | null;
  /**
   * The position both views are pointing at, as a distance along the section, or
   * null when neither is. Marked here as a vertical rule.
   */
  hoveredDistanceFt?: number | null;
  /**
   * Notified when a click lands on a well — a bore or a crossing — with its uwi, and
   * with null when it lands on open plot, which is how a selection is put away.
   */
  onWellPick?: (uwi: string | null) => void;
  /** Called with the distance under the pointer, or null when it leaves the plot. */
  onHoverDistanceChange?: (distanceFt: number | null) => void;
  /**
   * Called with the distance range on screen whenever it changes, so the section can
   * ask for that stretch at a finer sample than the whole line could afford.
   */
  onVisibleRangeChange?: (range: [number, number]) => void;
  isLoading?: boolean;
  height?: number | string;
}

// Reset-zoom and image export are the drawer header's to own — the mock puts both
// there, with the export behind a panel of its own — so the view's built-in
// versions stay off rather than sitting on the canvas as a second copy.

/**
 * The section in profile: the horizon stack against depth, over the distance the
 * line covers.
 *
 * Everything that decides what gets drawn lives in `buildProfilePlan`, which is
 * plain data — this component only puts that plan onto a SciChart surface. A line
 * is drawn as one series per unbroken run, because a missing depth is a gap in the
 * grid rather than a value, and `XyDataSeries` rejects NaN so there is no way to
 * express the break inside a single series.
 *
 * @param {CrossSectionProfileChartProps} props - Component props.
 * @return {JSX.Element} The profile chart.
 */
export const CrossSectionProfileChart: FC<CrossSectionProfileChartProps> = ({
  series,
  ground = null,
  showGround = false,
  vertices = [],
  xRange = null,
  depthAxisTitle,
  invertDepthAxis = false,
  flattened = false,
  distanceUnit = 'ft',
  flipped = false,
  showLabels = true,
  guideEnds = false,
  showFills = false,
  resetSignal = 0,
  axisZoom = false,
  crossings = [],
  faults = [],
  sticks = [],
  boundaries = [],
  focusedWellUwi = null,
  hoveredDistanceFt = null,
  onWellPick,
  onHoverDistanceChange,
  onVisibleRangeChange,
  isLoading = false,
  height = '100%',
}) => {
  // What the depth axis was last fitted to. SciChart fits on first draw and then leaves
  // the range alone — right while the user pans, wrong the moment the plot gains
  // something outside it: switching the ground line on put it off the top of the plot.
  const depthFitRef = useRef('');

  // What the distance axis was last pinned for. See the note where it is used.
  const distanceSpanRef = useRef('');

  // Read inside the SciChart callbacks, which close over their first render.
  // Read inside the canvas listener, which is registered once with the surface.
  const hoverRef = useRef(onHoverDistanceChange);
  hoverRef.current = onHoverDistanceChange;

  // Read inside the canvas click handler, which is registered once with the surface.
  const pickRef = useRef(onWellPick);
  pickRef.current = onWellPick;

  // Read inside the axis subscription, which is made once with the surface.
  const rangeRef = useRef(onVisibleRangeChange);
  rangeRef.current = onVisibleRangeChange;

  // What the pointer is on, for the tooltip. Local to the chart: it is a lens over
  // what is already drawn, so it must not re-stage anything or touch shared state.
  // The surface, for the pieces that update without a re-stage.
  const surfaceRef =
    useRef<
      Parameters<
        NonNullable<Parameters<typeof SciChartView>[0]['onSurfaceReady']>
      >[0]
    >(null);

  // Every well symbol the plot needs, drawn once per symbol-and-colour and kept: the
  // same status recurs across a section, and a section is re-staged often.
  const spriteRef = useRef(new Map<string, HTMLImageElement>());
  // Bumped when a symbol finishes drawing, which is what puts it on the plot: a
  // canvas has to be decoded into an image before SciChart can use it, and staging is
  // synchronous. Until then the well is drawn as a plain marker rather than not at all.
  const [spriteGeneration, setSpriteGeneration] = useState(0);

  // What the wells on the section ask for, as symbol-and-colour pairs.
  const neededSprites = useMemo(() => {
    const wanted = new Map<string, { symbol: WellSymbol; color: string }>();
    const want = (
      status: string | null,
      color: string,
      workspaceEdited = false,
      geometrySourceKind: string | null = null
    ) => {
      const symbol = symbolForCrossing(
        status,
        workspaceEdited,
        geometrySourceKind
      );
      wanted.set(wellSymbolKey(symbol, color), { symbol, color });
    };
    crossings.forEach((crossing) =>
      want(
        crossing.status ?? null,
        crossing.color,
        crossing.workspaceEdited,
        crossing.geometrySourceKind
      )
    );
    sticks.forEach((stick) => want(stick.status, stick.color));
    return wanted;
  }, [crossings, sticks]);

  useEffect(() => {
    const missing = [...neededSprites].filter(
      ([key]) => !spriteRef.current.has(key)
    );
    if (missing.length === 0) {
      return undefined;
    }
    let live = true;
    Promise.all(
      missing.map(([key, spec]) =>
        wellSymbolImage({ ...spec, pixelRatio: DpiHelper.PIXEL_RATIO })
          .then((image) => [key, image] as const)
          // A symbol that will not draw leaves the well on its fallback marker, which
          // is a well drawn plainly rather than a well missing.
          .catch(() => null)
      )
    ).then((drawn) => {
      if (!live) {
        return;
      }
      const arrived = drawn.filter(
        (entry): entry is readonly [string, HTMLImageElement] => entry !== null
      );
      if (arrived.length === 0) {
        return;
      }
      arrived.forEach(([key, image]) => spriteRef.current.set(key, image));
      setSpriteGeneration((generation) => generation + 1);
    });
    return () => {
      live = false;
    };
  }, [neededSprites]);

  /**
   * The marker a well is drawn with: its own symbol where that symbol is ready, and a
   * plain circle until it is.
   *
   * @param {TSciChart} wasmContext - The chart's context.
   * @param {object} well - The well's status and colour.
   * @return {BasePointMarker} The marker.
   */
  const wellMarker = (
    wasmContext: SciChartInitContext['wasmContext'],
    {
      status,
      color,
      workspaceEdited = false,
      geometrySourceKind = null,
    }: {
      status: string | null;
      color: string;
      workspaceEdited?: boolean;
      geometrySourceKind?: string | null;
    }
  ) => {
    const symbol = symbolForCrossing(
      status,
      workspaceEdited,
      geometrySourceKind
    );
    const size = wellSymbolSize(symbol);
    const sprite = spriteRef.current.get(wellSymbolKey(symbol, color));
    if (sprite) {
      return new SpritePointMarker(wasmContext, {
        width: size,
        height: size,
        image: sprite,
      });
    }
    // Sized to the sprite's BODY, not to its box. A sprite insets its shape so the
    // strokes that reach past it have room, so a plain circle at the full box diameter
    // sits half again as large as the sprited well beside it.
    const bodySize = size * BODY_RADIUS_SHARE * 2;
    return new EllipsePointMarker(wasmContext, {
      width: bodySize,
      height: bodySize,
      fill: isFilledSymbol(symbol) ? color : 'transparent',
      stroke: MARKER_STROKE_COLOR,
      strokeThickness: 1,
    });
  };

  // The interval names, each with the grid it names. Held so where they sit and which
  // of them fit can be settled again on a pan or a zoom, without re-staging the plot.
  // The county rules, kept so their upper end can follow the top of the view.
  const countyRuleRef = useRef<
    Array<{ annotation: LineAnnotation; fromDepthFt: number | null }>
  >([]);

  const labelRef = useRef<
    Array<{ annotation: NativeTextAnnotation; label: PlanLabel }>
  >([]);

  /**
   * Puts each interval name on its own horizon at the edge of the view, and hides the
   * ones the depth on screen has no room for.
   *
   * Two things follow the view. The name rides the visible edge rather than the
   * section's own end, so zooming into the middle of a long section does not leave the
   * names behind it — clamped to the stretch its grid actually covers, so panning past
   * that stretch takes the name away instead of floating it over ground the grid never
   * reached. And its depth is read off the horizon wherever it lands, so the name
   * stays against its line as the line dips.
   *
   * Room is measured against the plot's real height rather than a fixed share of the
   * depth on show. The two only agree at one drawer height, and the drawer opens
   * shorter than the one that share was chosen for — which is how a tightly stacked
   * Spraberry section came to print three names over each other. Reading the axis's own
   * pixel length instead makes the rule hold at any zoom and any drawer height.
   * Shallowest first, so the stack reads down the way the section does.
   *
   * @param {AxisBase2D} distanceAxis - The distance axis.
   * @param {AxisBase2D} depthAxis - The depth axis.
   */
  /**
   * Stands each county rule up from the ground to the top of the view.
   *
   * The upper end is a position on screen rather than a depth, so it is re-read whenever
   * the view moves — a rule that stopped where the top used to be would sink into the
   * plot on the next zoom. On a flattened section there is no ground on the axis, so the
   * rule spans the whole height instead of starting nowhere.
   *
   * @param {AxisBase2D} depthAxis - The depth axis.
   */
  const placeCountyRules = (depthAxis: AxisBase2D): void => {
    const down = depthAxis.visibleRange;
    if (down === undefined) {
      return;
    }
    // Which end of the range is drawn at the top depends on which way the axis counts:
    // TVDSS rises up the plot, TVD falls down it.
    const top = depthAxis.flippedCoordinates ? down.min : down.max;
    const bottom = depthAxis.flippedCoordinates ? down.max : down.min;

    countyRuleRef.current.forEach(({ annotation, fromDepthFt }) => {
      annotation.y1 = fromDepthFt ?? bottom;
      annotation.y2 = top;
    });
  };

  const placeLabels = (
    distanceAxis: AxisBase2D,
    depthAxis: AxisBase2D
  ): void => {
    const across = distanceAxis.visibleRange;
    const down = depthAxis.visibleRange;
    if (across === undefined || down === undefined) {
      return;
    }
    const inset = (across.max - across.min) * LABEL_VIEW_INSET_FRACTION;
    // A line of text in the axis's own values: the share of the plot's height one line
    // takes, times the depth it covers. Device pixels both sides, so they divide
    // cleanly. Zero height means no layout yet — keep every name for the next frame.
    const plotPx = depthAxis.axisLength;
    const linePx = INTERVAL_LABEL_GAP_PX * DpiHelper.PIXEL_RATIO;
    const gap = plotPx > 0 ? (down.max - down.min) * (linePx / plotPx) : 0;
    // The top row belongs to the section's letters and county names, pinned to a share
    // of the height rather than a depth — so it is converted to one before an interval
    // name can be kept out. Which end is the top depends on the axis's direction.
    const rowShare = GUIDE_LETTER_Y + linePx / Math.max(plotPx, 1);
    const span = down.max - down.min;
    const flipped = depthAxis.flippedCoordinates;
    const reserved = flipped
      ? down.min + span * rowShare
      : down.max - span * rowShare;
    let lastShown: number | null = null;

    labelRef.current.forEach(({ annotation, label }) => {
      // Off the visible window entirely, or off the grid's own stretch: nothing to
      // label here.
      if (label.toFt < across.min || label.fromFt > across.max) {
        annotation.isHidden = true;
        return;
      }
      const edge = label.anchorRight ? across.max - inset : across.min + inset;
      const at = Math.min(Math.max(edge, label.fromFt), label.toFt);
      const depthFt = depthAtDistance(label.points, at);
      if (depthFt === null) {
        annotation.isHidden = true;
        return;
      }
      const clearOfRow =
        gap <= 0 || (flipped ? depthFt >= reserved : depthFt <= reserved);
      const clear =
        clearOfRow &&
        (gap <= 0 ||
          lastShown === null ||
          Math.abs(depthFt - lastShown) >= gap);
      annotation.isHidden = !clear;
      if (!clear) {
        return;
      }
      lastShown = depthFt;
      annotation.x1 = at;
      annotation.y1 = depthFt;
    });
  };

  // The shared-position guide, drawn imperatively: it moves on every mousemove —
  // from the plot or from the map — and re-staging the whole surface per move is
  // what made the map-side hover invisible on a dense section.
  const guideRef = useRef<VerticalLineAnnotation | null>(null);
  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) {
      return;
    }
    if (guideRef.current) {
      try {
        surface.annotations.remove(guideRef.current);
      } catch {
        // A re-stage cleared it already; nothing to remove.
      }
      guideRef.current = null;
    }
    if (hoveredDistanceFt !== null) {
      const guide = new VerticalLineAnnotation({
        x1: hoveredDistanceFt,
        stroke: INDICATOR_STROKE,
        strokeThickness: 1,
        strokeDashArray: [4, 3],
      });
      surface.annotations.add(guide);
      guideRef.current = guide;
    }
  }, [hoveredDistanceFt]);

  const [hoverTip, setHoverTip] = useState<{
    xPx: number;
    yPx: number;
    flipX: boolean;
    flipY: boolean;
    groups: HoverGroup[];
  } | null>(null);

  const stateRef = useRef({
    plan: {} as ProfilePlan,
    hoverContent: { series, ground, sticks, crossings },
    resetSignal,
    depthAxisTitle,
    invertDepthAxis,
    distanceUnit,
    flipped,
    focusedWellUwi,
  });
  // Memoised because the hovered distance arrives as a prop: without this, moving the
  // pointer across the map rebuilds the whole plan — every sample, band run, label
  // spread and county stretch — for a frame that draws the same thing.
  const plan = useMemo(
    () =>
      buildProfilePlan({
        series,
        ground,
        showGround,
        flattened,
        invertDepthAxis,
        vertices,
        xRange,
        flipped,
        showLabels,
        guideEnds,
        showFills,
        crossings,
        faults,
        sticks,
        boundaries,
      }),
    [
      series,
      ground,
      showGround,
      flattened,
      invertDepthAxis,
      vertices,
      xRange,
      flipped,
      showLabels,
      guideEnds,
      showFills,
      crossings,
      faults,
      sticks,
      boundaries,
    ]
  );
  stateRef.current = {
    hoverContent: {
      series,
      ground: showGround ? ground : null,
      sticks,
      // The plan's crossings rather than the caller's: a crossing at a nominal depth
      // is drawn where the plan puts it, and the pointer has to find it there.
      crossings: plan.crossings,
    },
    resetSignal,
    plan,
    depthAxisTitle,
    invertDepthAxis,
    distanceUnit,
    flipped,
    focusedWellUwi,
  };

  const initChart = ({ surface, wasmContext }: SciChartInitContext) => {
    const distanceAxis = new NumericAxis(wasmContext, {
      axisTitle: `Distance along section (${stateRef.current.distanceUnit})`,
      axisTitleStyle: { fontSize: AXIS_TITLE_FONT_SIZE },
      labelStyle: { fontSize: AXIS_LABEL_FONT_SIZE },
      drawMinorGridLines: false,
      growBy: new NumberRange(0.02, 0.02),
    });
    // Reads the ref, so switching units only relabels the axis rather than
    // rebuilding the surface, which would throw away the zoom.
    distanceAxis.labelProvider.formatLabel = (value: number) =>
      formatSectionDistance(
        value,
        stateRef.current.distanceUnit,
        Math.abs(distanceAxis.visibleRange.max - distanceAxis.visibleRange.min)
      );
    distanceAxis.flippedCoordinates = stateRef.current.flipped;
    // Every zoom and pan of the plot, reported as a distance range. The section reads
    // it to decide whether the stretch on screen is worth re-sampling.
    distanceAxis.visibleRangeChanged.subscribe((args) => {
      const range = args?.visibleRange;
      if (range) {
        // Mile precision follows the zoom, and the provider caches per tick value —
        // without this a re-zoomed axis serves labels formatted for the old window.
        invalidateLabels(distanceAxis);
        // Interval names ride the visible edge, so a sideways pan moves them while
        // leaving the depth range alone. Read the depth axis here rather than closing
        // over it: this fires while the axes are still being added.
        const depth = surface.yAxes.get(0);
        if (depth) {
          placeLabels(distanceAxis, depth);
        }
        rangeRef.current?.([range.min, range.max]);
      }
    });
    surface.xAxes.add(distanceAxis);

    const depthAxis = new NumericAxis(wasmContext, {
      axisTitle: stateRef.current.depthAxisTitle,
      axisTitleStyle: { fontSize: AXIS_TITLE_FONT_SIZE },
      labelStyle: { fontSize: AXIS_LABEL_FONT_SIZE },
      // Once, not Always: the first cut sets the frame, and everything after it
      // keeps whatever the user has zoomed to.
      autoRange: EAutoRange.Once,
      drawMinorGridLines: false,
      growBy: new NumberRange(DEPTH_MARGIN, DEPTH_MARGIN),
    });
    depthAxis.labelProvider.formatLabel = (value: number) =>
      formatAxisTick(value, 0);
    depthAxis.flippedCoordinates = stateRef.current.invertDepthAxis;
    // Zooming separates the horizons, so names that had no room find it. Only visibility
    // is settled here — nothing is re-staged, which keeps a zoom cheap on a dense
    // section. County rules follow too: their upper end is the top of the view.
    depthAxis.visibleRangeChanged.subscribe(() => {
      placeLabels(distanceAxis, depthAxis);
      placeCountyRules(depthAxis);
    });
    surface.yAxes.add(depthAxis);

    surface.chartModifiers.add(
      new ZoomPanModifier(),
      new MouseWheelZoomModifier(),
      new ZoomExtentsModifier()
    );
    if (axisZoom) {
      // The gutter is outside the series rect, so this costs the plot-area pan nothing.
      surface.chartModifiers.add(
        new XAxisDragModifier(),
        new YAxisDragModifier()
      );
    }

    // Hovering the plot marks the corresponding point on the map. Reported as a
    // distance rather than as a pixel: the map has its own idea of where that is.
    const canvas = surface.domCanvas2D;
    if (canvas) {
      const report = (event: MouseEvent) => {
        const bounds = canvas.getBoundingClientRect();
        const axis = surface.xAxes.get(0);
        const at = axis.getCurrentCoordinateCalculator?.();
        if (!at) {
          return;
        }
        const { x: cssX, y: cssY } = plotPointFrom(surface, canvas, event);
        const distanceFt = at.getDataValue(cssX * DpiHelper.PIXEL_RATIO);
        hoverRef.current?.(distanceFt);

        // The tooltip: whatever is drawn within reach of the pointer. Coordinate
        // calculators answer in device pixels, so everything is brought back to the
        // CSS pixels the reach is defined in.
        const depthCalc = surface.yAxes
          .get(0)
          .getCurrentCoordinateCalculator?.();
        if (!depthCalc) {
          return;
        }
        const groups = hoverGroupsAt(
          stateRef.current.hoverContent,
          distanceFt,
          cssY,
          (depthFt) => depthCalc.getCoordinate(depthFt) / DpiHelper.PIXEL_RATIO
        );
        setHoverTip(
          groups.length === 0
            ? null
            : {
                xPx: cssX,
                yPx: cssY,
                // Just off the pointer, flipped to its other side near an edge —
                // the mock's own rule — so it never runs off the plot.
                flipX: cssX > bounds.width - 260,
                flipY: cssY > bounds.height - 190,
                groups,
              }
        );
      };
      const clear = () => {
        hoverRef.current?.(null);
        setHoverTip(null);
      };
      canvas.addEventListener('mousemove', report);
      canvas.addEventListener('mouseleave', clear);

      // A click picks the well it lands on, told from a pan by press and release landing
      // within a few pixels. Pointer events because the surface prevents the default on
      // pointerdown; on the wrapper because pan capture leaves only an ancestor the release.
      const clickHost = canvas.parentElement ?? canvas;
      let pressAt: { x: number; y: number } | null = null;
      clickHost.addEventListener('pointerdown', (event: PointerEvent) => {
        pressAt = { x: event.clientX, y: event.clientY };
      });
      clickHost.addEventListener('pointerup', (event: PointerEvent) => {
        const from = pressAt;
        pressAt = null;
        if (
          !from ||
          Math.hypot(event.clientX - from.x, event.clientY - from.y) > 4
        ) {
          return;
        }
        const at = surface.xAxes.get(0).getCurrentCoordinateCalculator?.();
        const depthCalc = surface.yAxes
          .get(0)
          .getCurrentCoordinateCalculator?.();
        if (!at || !depthCalc) {
          return;
        }
        const pointer = plotPointFrom(surface, canvas, event);
        pickRef.current?.(
          wellAt(
            stateRef.current.hoverContent,
            pointer.x,
            pointer.y,
            (ft: number) => at.getCoordinate(ft) / DpiHelper.PIXEL_RATIO,
            (depthFt: number) =>
              depthCalc.getCoordinate(depthFt) / DpiHelper.PIXEL_RATIO
          )
        );
      });
    }
  };

  const renderData = ({ surface, wasmContext }: SciChartInitContext) => {
    surface.renderableSeries.clear();
    surface.annotations.clear();

    const { plan } = stateRef.current;

    const distanceAxis = surface.xAxes.get(0);
    const depthAxis = surface.yAxes.get(0);
    distanceAxis.axisTitle = `Distance along section (${stateRef.current.distanceUnit})`;
    distanceAxis.flippedCoordinates = stateRef.current.flipped;
    depthAxis.axisTitle = stateRef.current.depthAxisTitle;
    depthAxis.flippedCoordinates = stateRef.current.invertDepthAxis;

    // Both label providers cache their text per tick value, so a unit switch or a
    // change of datum relabels nothing until the cache is dropped — the axis would
    // keep reading in feet under a title that says miles.
    invalidateLabels(distanceAxis);
    invalidateLabels(depthAxis);

    // Pinned when the extent it should show changes — a new line, a crop, a reset — and
    // left alone otherwise. Re-pinning on every re-stage threw the user's zoom away
    // whenever anything else was toggled.
    const spanKey = [
      stateRef.current.resetSignal,
      plan.xRange ? plan.xRange.join(':') : 'auto',
    ].join('|');

    if (plan.xRange && spanKey !== distanceSpanRef.current) {
      distanceSpanRef.current = spanKey;
      distanceAxis.autoRange = EAutoRange.Never;
      distanceAxis.visibleRange = new NumberRange(
        plan.xRange[0],
        plan.xRange[1]
      );
    }

    // Reset is a re-stage, not a re-cut: the distance axis returns to the section's
    // extent and the depth axis re-fits. Done here rather than by `autoRange`, which
    // only fires on first draw — and a pan is no change in what the axis must cover.
    const fitKey = [
      stateRef.current.resetSignal,
      stateRef.current.depthAxisTitle,
      plan.ground.length,
      plan.lines.length,
      plan.sticks.length,
      plan.crossings.length > 0,
    ].join('|');

    if (fitKey !== depthFitRef.current) {
      depthFitRef.current = fitKey;
      const extent = depthExtentOf(plan);
      if (extent) {
        const [low, high] = extent;
        const span = high - low;
        // The same breathing room the axis was built with, and a floor for the case
        // where everything sits at one depth.
        const base = Math.max(span * DEPTH_MARGIN, 100);
        // A bore writes its position, name and uwi above its own top, so the plot
        // needs room for text that is not data. Which numeric end that is depends on
        // the reference: TVDSS puts the shallowest value at the top, TVD inverts.
        const headroom =
          plan.sticks.length > 0
            ? Math.max(span * STICK_LABEL_HEADROOM, 400)
            : 0;
        const inverted = stateRef.current.invertDepthAxis;
        depthAxis.autoRange = EAutoRange.Never;
        depthAxis.visibleRange = new NumberRange(
          low - base - (inverted ? headroom : 0),
          high + base + (inverted ? 0 : headroom)
        );
      }
    }

    const addRuns = (
      runs: ProfilePlan['ground'],
      stroke: string,
      thickness: number
    ) => {
      runs.forEach((run) => {
        surface.renderableSeries.add(
          new FastLineRenderableSeries(wasmContext, {
            stroke,
            strokeThickness: thickness,
            dataSeries: new XyDataSeries(wasmContext, {
              xValues: run.xValues,
              yValues: run.yValues,
            }),
          })
        );
      });
    };

    // Fills first, then ground, then the horizons: each is drawn over the thing it
    // would otherwise hide.
    plan.bands.forEach((band) => {
      band.runs.forEach((run) => {
        surface.renderableSeries.add(
          new FastBandRenderableSeries(wasmContext, {
            dataSeries: new XyyDataSeries(wasmContext, {
              xValues: run.xValues,
              yValues: run.topValues,
              y1Values: run.baseValues,
            }),
            fill: withOpacity(band.color, FILL_OPACITY),
            fillY1: withOpacity(band.color, FILL_OPACITY),
            // The bounding horizons are drawn as their own lines, so an outline here
            // would double every one of them.
            strokeThickness: 0,
          })
        );
      });
    });

    addRuns(plan.ground, GROUND_STROKE, LINE_THICKNESS);
    plan.lines.forEach((line) => {
      addRuns(line.runs, line.color, LINE_THICKNESS);
    });

    // Faults first: the band is context the horizons are read against. A fault is
    // subsurface, so it hangs from the terrain rather than standing into the sky, with
    // its foot well below everything drawn — the view moves, the annotation is staged once.
    const belowEverything = deepEdge(plan, stateRef.current.invertDepthAxis);
    plan.faults.forEach((fault) => {
      const clipped = fault.topDepthFt !== null && belowEverything !== null;
      surface.annotations.add(
        new BoxAnnotation({
          x1: fault.startFt,
          x2: fault.endFt,
          y1: clipped ? (fault.topDepthFt as number) : 0,
          y2: clipped ? belowEverything : 1,
          yCoordinateMode: clipped
            ? ECoordinateMode.DataValue
            : ECoordinateMode.Relative,
          fill: FAULT_FILL,
          stroke: FAULT_STROKE,
          strokeThickness: 1,
        })
      );
    });

    // County lines are drawn in the air above the ground, not the rock below, each rising
    // from the terrain (or sea level) to the top of the plot. A plain line, because
    // SciChart's vertical one draws from the axis edge to `y1` and refuses a y2.
    countyRuleRef.current = plan.countyRules.map((rule) => {
      const annotation = new LineAnnotation({
        x1: rule.distanceFt,
        x2: rule.distanceFt,
        y1: rule.fromDepthFt ?? 0,
        y2: rule.fromDepthFt ?? 0,
        stroke: COUNTY_STROKE,
        strokeThickness: 1,
      });
      surface.annotations.add(annotation);
      return { annotation, fromDepthFt: rule.fromDepthFt };
    });

    plan.counties.forEach((county) => {
      surface.annotations.add(
        new NativeTextAnnotation({
          x1: county.distanceFt,
          y1: COUNTY_LABEL_Y,
          xCoordinateMode: ECoordinateMode.DataValue,
          yCoordinateMode: ECoordinateMode.Relative,
          text: county.label,
          fontSize: COUNTY_LABEL_FONT_SIZE,
          textColor: COUNTY_LABEL_COLOR,
          horizontalAnchorPoint: EHorizontalAnchorPoint.Center,
          verticalAnchorPoint: EVerticalAnchorPoint.Top,
        })
      );
    });

    // The defining wells' bores. Drawn over the horizons they penetrate and under the
    // crossing markers, so a marker sitting on a bore stays readable.
    plan.sticks.forEach((stick) => {
      const focused = stick.uwi === stateRef.current.focusedWellUwi;
      surface.renderableSeries.add(
        new FastLineRenderableSeries(wasmContext, {
          dataSeries: new XyDataSeries(wasmContext, {
            xValues: [stick.distanceFt, stick.distanceFt],
            yValues: [stick.topFt, stick.bottomFt],
            dataIsSortedInX: false,
          }),
          stroke: focused ? FOCUS_STROKE : stick.color,
          strokeThickness: focused ? STICK_THICKNESS + 1 : STICK_THICKNESS,
        })
      );

      // The status colour, at the well's own surface: the bore's identity rather
      // than a description of it, so it survives the names being switched off.
      surface.renderableSeries.add(
        new XyScatterRenderableSeries(wasmContext, {
          dataSeries: new XyDataSeries(wasmContext, {
            xValues: [stick.distanceFt],
            yValues: [stick.topFt],
          }),
          // The same standard symbol a crossing draws: one well, one shape, wherever
          // the section shows it.
          pointMarker: wellMarker(wasmContext, {
            status: stick.status,
            color: stick.color,
          }),
        })
      );

      // Total depth, ticked across the bore's foot the way a well's TD reads on a
      // gun barrel: the end of the hole rather than the end of a line.
      surface.renderableSeries.add(
        new XyScatterRenderableSeries(wasmContext, {
          dataSeries: new XyDataSeries(wasmContext, {
            xValues: [stick.distanceFt],
            yValues: [stick.bottomFt],
          }),
          pointMarker: new SquarePointMarker(wasmContext, {
            width: STICK_TD_TICK_WIDTH,
            height: STICK_TD_TICK_HEIGHT,
            fill: focused ? FOCUS_STROKE : stick.color,
            strokeThickness: 0,
          }),
        })
      );

      // The position, then whatever the room allows, stacked up from the bore's top so
      // the well reads top-down. On a dense traverse the plan degrades this rung by rung
      // — name and uwi, name, the number, nothing — and an empty label is not drawn.
      const lines = [
        ...(stick.showPosition ? [String(stick.position)] : []),
        ...stick.nameLines,
      ];
      if (lines.length === 0) {
        return;
      }
      surface.annotations.add(
        new NativeTextAnnotation({
          x1: stick.distanceFt,
          y1: stick.topFt,
          text: lines.join('\n'),
          fontSize: STICK_LABEL_FONT_SIZE,
          textColor: MARKER_STROKE_COLOR,
          horizontalAnchorPoint: STICK_LABEL_ANCHORS[stick.labelAnchor],
          // Above its own top end, where the bore leaves the label room — lifted a
          // little further so the bottom line clears the class symbol itself.
          verticalAnchorPoint: EVerticalAnchorPoint.Bottom,
          padding: new Thickness(0, 0, STICK_LABEL_LIFT_PX, 0),
        })
      );
    });

    // Crossings, as one scatter per well so a focused well can be emphasised
    // whole — every crossing of one well is the same well.
    const byWell = new Map<string, ProfileCrossing[]>();
    plan.crossings.forEach((crossing) => {
      if (crossing.depthFt === null) {
        return;
      }
      // The section's own Well Workspace's edit of a bore is a well of its own here:
      // drawn with its own symbol beside the filed trajectory, not folded into it.
      const key = crossingSeriesKey(crossing);
      byWell.set(key, [...(byWell.get(key) ?? []), crossing]);
    });
    byWell.forEach((wellCrossings) => {
      // Keyed per edit as well as per well, so the well's own uwi decides the focus.
      const focused = wellCrossings[0].uwi === stateRef.current.focusedWellUwi;
      const xValues = wellCrossings.map((crossing) => crossing.distanceFt);
      const yValues = wellCrossings.map(
        (crossing) => crossing.depthFt as number
      );

      // The focus ring goes behind the symbol rather than around it: the symbol is a
      // drawn shape now, and a spotlight has to leave the shape it lights alone.
      if (focused) {
        surface.renderableSeries.add(
          new XyScatterRenderableSeries(wasmContext, {
            dataSeries: new XyDataSeries(wasmContext, {
              xValues,
              yValues,
              dataIsSortedInX: false,
            }),
            pointMarker: new EllipsePointMarker(wasmContext, {
              width: WELL_SYMBOL_SIZE + FOCUS_RING_GROWTH,
              height: WELL_SYMBOL_SIZE + FOCUS_RING_GROWTH,
              fill: 'transparent',
              stroke: FOCUS_STROKE,
              strokeThickness: 2.5,
            }),
          })
        );
      }
      surface.renderableSeries.add(
        new XyScatterRenderableSeries(wasmContext, {
          dataSeries: new XyDataSeries(wasmContext, {
            xValues,
            yValues,
            dataIsSortedInX: false,
          }),
          pointMarker: wellMarker(wasmContext, {
            status: wellCrossings[0].status ?? null,
            color: wellCrossings[0].color,
            workspaceEdited: wellCrossings[0].workspaceEdited,
            geometrySourceKind: wellCrossings[0].geometrySourceKind,
          }),
        })
      );
    });

    // Interval names sit on their own horizon rather than being nudged into a ladder: a
    // name a few hundred feet off its line labels the wrong surface. A stack too tight
    // for every name shows the ones it can, and which those are follows the view.
    labelRef.current = [...plan.labels]
      .sort((first, second) => first.depthFt - second.depthFt)
      .map((label) => {
        const annotation = new NativeTextAnnotation({
          x1: label.distanceFt,
          y1: label.depthFt,
          text: label.text,
          fontSize: INTERVAL_LABEL_FONT_SIZE,
          textColor: label.color,
          horizontalAnchorPoint: label.anchorRight
            ? EHorizontalAnchorPoint.Right
            : EHorizontalAnchorPoint.Left,
          verticalAnchorPoint: EVerticalAnchorPoint.Bottom,
        });
        surface.annotations.add(annotation);
        return { annotation, label };
      });
    // Twice, and the second is the one that counts: an axis has no measured length until
    // SciChart lays the surface out, and the room between names is a share of it.
    // Placing now avoids a flash; placing next frame is when there is a height to use.
    placeLabels(distanceAxis, depthAxis);
    placeCountyRules(depthAxis);
    requestAnimationFrame(() => {
      if (surfaceRef.current === surface) {
        placeLabels(distanceAxis, depthAxis);
        placeCountyRules(depthAxis);
      }
    });

    // Intermediate vertices: a faint guide at the distance, with the map's own
    // letter pinned to the top of the plot.
    plan.guides.forEach((guide) => {
      surface.annotations.add(
        new VerticalLineAnnotation({
          x1: guide.distanceFt,
          stroke: GUIDE_STROKE,
          strokeThickness: 1,
          strokeDashArray: GUIDE_DASH,
          opacity: GUIDE_OPACITY,
        })
      );
      if (guide.label === null) {
        return;
      }
      surface.annotations.add(
        new NativeTextAnnotation({
          x1: guide.distanceFt,
          y1: GUIDE_LETTER_Y,
          xCoordinateMode: ECoordinateMode.DataValue,
          yCoordinateMode: ECoordinateMode.Relative,
          text: guide.label,
          fontSize: GUIDE_LETTER_FONT_SIZE,
          textColor: GUIDE_LETTER_COLOR,
          horizontalAnchorPoint: EHorizontalAnchorPoint.Center,
          verticalAnchorPoint: EVerticalAnchorPoint.Top,
        })
      );
    });

    // Which end of the map's line the profile is read from — the one thing it cannot show
    // for itself. Lettered AT the ends in distance values: pinned to the corners, the
    // letters stayed put while the section panned under them. Their row stays relative.
    plan.ends.forEach((end) => {
      surface.annotations.add(
        new NativeTextAnnotation({
          x1: end.distanceFt,
          y1: END_LABEL_Y,
          xCoordinateMode: ECoordinateMode.DataValue,
          yCoordinateMode: ECoordinateMode.Relative,
          text: end.label,
          fontSize: END_LABEL_FONT_SIZE,
          textColor: END_LABEL_COLOR,
          horizontalAnchorPoint: end.anchorRight
            ? EHorizontalAnchorPoint.Right
            : EHorizontalAnchorPoint.Left,
          verticalAnchorPoint: EVerticalAnchorPoint.Top,
        })
      );
    });
  };

  const tooltip = hoverTip && (
    <div
      className={styles.hoverTooltip}
      style={{
        left: hoverTip.xPx + (hoverTip.flipX ? -12 : 12),
        top: hoverTip.yPx + (hoverTip.flipY ? -12 : 12),
        transform: `translate(${hoverTip.flipX ? '-100%' : '0'}, ${
          hoverTip.flipY ? '-100%' : '0'
        })`,
      }}
    >
      {hoverTip.groups.map((group) => (
        <div key={group.label} className={styles.hoverGroup}>
          <span className={styles.hoverHeader}>
            <span
              className={styles.hoverSwatch}
              style={{ background: group.color }}
            />
            {group.label}
          </span>
          {group.pairs.map((pair) => (
            <span key={pair.key} className={styles.hoverPair}>
              <span className={styles.hoverKey}>{pair.key}</span>
              {hoverValue(pair.valueFt)}
            </span>
          ))}
        </div>
      ))}
    </div>
  );

  // The picked well's card, pinned where the mock pins it. A defining well reads as its
  // position and name with class and depth; a crossing well reads as its crossings, each
  // in both references. The × puts the selection away, like clicking open plot.
  const pickedStick = focusedWellUwi
    ? (sticks.find((stick) => stick.uwi === focusedWellUwi) ?? null)
    : null;
  const pickedCrossings = focusedWellUwi
    ? crossings.filter((crossing) => crossing.uwi === focusedWellUwi)
    : [];
  const card = (pickedStick || pickedCrossings.length > 0) && (
    <div className={styles.wellCard}>
      <div className={styles.wellCardHead}>
        <span className={styles.wellCardTitle}>
          {/* A new tab, deliberately: the section is the user's own work and
              navigating away from it would throw the drawn line away. */}
          <Link
            href={`/wells/${focusedWellUwi}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.wellCardName}
          >
            {pickedStick
              ? `${pickedStick.position}. ${
                  pickedStick.name ?? pickedStick.uwi
                }`
              : (pickedCrossings[0].label ?? pickedCrossings[0].uwi)}
          </Link>
          <span className={styles.wellCardUwi}>{focusedWellUwi}</span>
        </span>
        <button
          type="button"
          className={styles.wellCardClose}
          aria-label="Clear selected well"
          onClick={() => onWellPick?.(null)}
        >
          ×
        </button>
      </div>
      {pickedStick?.status && (
        <span className={styles.hoverPair}>
          <span className={styles.hoverKey}>Class</span>
          {pickedStick.status}
        </span>
      )}
      {pickedStick !== null && pickedStick.totalDepthFt !== null && (
        <span className={styles.hoverPair}>
          <span className={styles.hoverKey}>Total depth</span>
          {hoverValue(pickedStick.totalDepthFt)}
        </span>
      )}
      {pickedCrossings.map((crossing, index) => (
        <div
          key={`${crossing.distanceFt}-${index}`}
          className={styles.hoverGroup}
        >
          {pickedCrossings.length > 1 && (
            <span className={styles.wellCardCrossing}>
              {`Crossing ${index + 1} · ${formatSectionDistance(
                crossing.distanceFt,
                distanceUnit
              )} ${distanceUnit} along`}
            </span>
          )}
          {/* Which class of well this is — the thing its symbol stands for. Said once
              per crossing rather than once per card, since a well crossing twice is
              still one well and each crossing carries it. */}
          {index === 0 && crossing.status && (
            <span className={styles.hoverPair}>
              <span className={styles.hoverKey}>Class</span>
              {crossing.status}
            </span>
          )}
          {/* A depth nobody measured is not a depth to report. The well is drawn at the
              surface for want of anywhere truer, and the backend's stand-in thousand feet
              would be read here as a measurement of this well. */}
          <span className={styles.hoverPair}>
            <span className={styles.hoverKey}>TVD</span>
            {hoverValue(
              hasNoMeasuredDepth(crossing) ? null : (crossing.tvdFt ?? null)
            )}
          </span>
          <span className={styles.hoverPair}>
            <span className={styles.hoverKey}>TVDSS</span>
            {hoverValue(
              hasNoMeasuredDepth(crossing) ? null : (crossing.tvdssFt ?? null)
            )}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <SciChartView
      initChart={initChart}
      renderData={renderData}
      renderDeps={[
        series,
        ground,
        showGround,
        vertices,
        xRange,
        depthAxisTitle,
        invertDepthAxis,
        distanceUnit,
        flipped,
        showLabels,
        guideEnds,
        showFills,
        resetSignal,
        crossings,
        faults,
        sticks,
        boundaries,
        focusedWellUwi,
        // A symbol finishing its draw is a change in what can be drawn, so the wells
        // waiting on it are staged again with it.
        spriteGeneration,
      ]}
      isLoading={isLoading}
      height={height}
      onSurfaceReady={(surface) => {
        surfaceRef.current = surface;
      }}
    >
      {tooltip}
      {card}
    </SciChartView>
  );
};
